import { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, DatePicker, Select, Input } from 'antd';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/shallow';
import { API } from '@services/api.service';
import { OPERATION } from '@/constants';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useNotify } from '@/hooks';
import { cn } from '@/libs/utils';
import { CURRENCY_OPTIONS, QUERY_KEY } from '@/shared/constants';
import { useSalaryStore } from '@/store/useSalaryStore';
import {
  DATE_FORMATS,
  formatDateForBackend,
  isEmployeeRequestFormDisabled,
} from '@/utils/formUtils';
import { formValidateAndSetNumber } from '@/utils/inputApi';
import { translateOptions } from '@/utils/utils';
import CustomDatePicker from '../input/CustomDatePicker';
import FormActionButton from '../input/FormActionButton';

export default function BonusForm({
  formData,
  typeOfLeave,
  employeeId,
  handleRefetch,
}) {
  const { t } = useTranslation(['menus', 'forms', 'messages']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const notification = useNotify();

  const { toggle, setToggle } = useSalaryStore(
    useShallow((state) => ({
      toggle: state.toggle,
      setToggle: state.setToggle,
    }))
  );

  const handleCancelModal = () => {
    if (toggle.leaveExtender) {
      setToggle('leaveExtender', false);

      handleRefetch();
    }
  };

  const currencyOptions = useMemo(() => {
    return translateOptions(CURRENCY_OPTIONS, t);
  }, [t]);

  // Check if company has bonus variations enabled
  const hasBonusVariations = useMemo(() => {
    return (
      formData?.company?.bonusVariations?.enabled &&
      formData?.company?.bonusVariations?.types?.length > 0
    );
  }, [formData?.company?.bonusVariations]);

  // Get bonus variation type options
  const bonusVariationOptions = useMemo(() => {
    if (!hasBonusVariations) return [];
    return formData.company.bonusVariations.types.map((type) => ({
      value: type.key,
      label: type.label,
    }));
  }, [hasBonusVariations, formData?.company?.bonusVariations?.types]);

  // * Form instance
  const [form] = Form.useForm();

  // * Initial form data
  const initialValues = {
    dateOfLeave: [],
    dateOfRequestCreate: null,
    currencyBonus: formData?.company?.otherAllowances?.bonus?.label || 'RSD',
  };

  // * Enabled fetch data if ok
  const isEnabled = (queryParams, employeeId) => {
    const { action, requestId } = queryParams;
    const hasAction = [OPERATION.view, OPERATION.edit].includes(action);
    return !!requestId && !!employeeId && !!hasAction;
  };

  // * Fetch data for (view or update)
  const { data: requestData } = useQuery({
    queryKey: [
      QUERY_KEY.request,
      employeeId,
      queryParams.action,
      queryParams.requestId,
    ],
    queryFn: async () => {
      return await API.requests.getRequestById(queryParams.requestId);
    },
    enabled: isEnabled(queryParams, employeeId),
    retry: false,
  });

  // * Show submitted action and disable input fields if not `ACCEPTED` or not `edit` mode
  const hasSubmitted = isEmployeeRequestFormDisabled(
    queryParams?.action ?? '',
    requestData
  );

  // * Set `initial data` from (update or view)
  useEffect(() => {
    if (requestData) {
      const {
        bonusMonth,
        valueBonus,
        currencyBonus,
        dateOfCreation,
        bonusVariationType,
      } = requestData;

      const currentYear = dayjs().year();
      const parsedDate = dayjs(`${bonusMonth} ${currentYear}`, 'MMMM YYYY');

      form.setFieldsValue({
        bonusMonth: bonusMonth ? parsedDate : null,
        valueBonus: valueBonus,
        currencyBonus: currencyBonus,
        dateOfRequestCreate: dateOfCreation ? dayjs(dateOfCreation) : null,
        bonusVariationType: bonusVariationType || undefined,
      });
    }
  }, [form, requestData]);

  // * Handle reset form
  const onReset = useCallback(() => {
    form.resetFields();
  }, [form]);

  // * After submitted form (create or edit) redirect to `route=hr-documents`
  const goToHrDocuments = useCallback(() => {
    setQueryParams({ ['route']: 'hr-documents', ['action']: undefined });
  }, [setQueryParams]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      if (queryParams.action === 'edit') {
        return await API.requests.updateRequestById(
          queryParams.requestId,
          payload
        );
      }

      return await API.requests.createNewRequest(payload);
    },
    onSuccess: (response) => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });

      // * Close modal if open your `LeaveExtenderModal` form modal
      handleCancelModal();

      // * Reset form fields
      onReset();

      // * Redirect to `route=hr-documents` after submitted form
      goToHrDocuments();
    },
    onError: (error) => {
      const errorMsg = error.response.data.error || error.message;

      notification.error({
        message: t('error', { ns: 'messages' }),
        description: errorMsg,
      });
    },
  });

  const onFinish = async (values) => {
    const { _id: employeeId, company, firstName, lastName } = formData;
    const {
      dateOfRequestCreate,
      bonusMonth,
      valueBonus,
      currencyBonus,
      bonusVariationType,
    } = values;

    const payload = {
      typeOfLeave,
      employee: employeeId,
      company: company._id,
      firstName,
      lastName,
      bonusMonth: formatDateForBackend(bonusMonth, DATE_FORMATS.STANDARD),
      currencyBonus: currencyBonus.toLowerCase(),
      valueBonus: valueBonus,
      dateOfCreation: formatDateForBackend(
        dateOfRequestCreate,
        DATE_FORMATS.STANDARD
      ),
    };

    // Add bonus variation if company has variations enabled and a type was selected
    if (hasBonusVariations && bonusVariationType) {
      const selectedType = formData.company.bonusVariations.types.find(
        (t) => t.key === bonusVariationType
      );
      payload.bonusVariationType = bonusVariationType;
      payload.bonusVariationLabel = selectedType?.label || '';
    }

    if (!hasSubmitted) {
      await mutateAsync(payload);
    } else {
      console.warn('Please check your hasSubmitted value', hasSubmitted);
    }
  };

  return (
    <Form
      form={form}
      name="employee_request-bonus-form"
      initialValues={initialValues}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className={cn('form grid gap-4')}
    >
      <div className="rounded-md border bg-zinc-100 p-4">
        {hasBonusVariations && (
          <>
            <Form.Item
              label={t('bonusVariationType', { ns: 'forms' })}
              name="bonusVariationType"
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
              ]}
            >
              <Select
                placeholder={t('selectBonusVariationType', { ns: 'forms' })}
                className="w-full"
                disabled={hasSubmitted}
                options={bonusVariationOptions}
              />
            </Form.Item>

            <hr className="mb-5" />
          </>
        )}

        <div className="grid gap-x-4 md:grid-cols-2">
          <Form.Item
            label={t('bonusForMonth', { ns: 'forms' })}
            name="bonusMonth"
            className="whitespace-nowrap"
            rules={[
              {
                required: true,
                message: t('formValidation.selectDate', { ns: 'forms' }),
              },
            ]}
          >
            <DatePicker
              picker="month"
              className="w-full"
              format={DATE_FORMATS.MONTH_YEAR}
              disabled={hasSubmitted}
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              label={t('value', { ns: 'forms' })}
              name="valueBonus"
              className="whitespace-nowrap"
              rules={[
                {
                  required: true,
                  message: t('formValidation.numberField.invalid', {
                    ns: 'forms',
                  }),
                },
              ]}
            >
              <Input
                min={0}
                type="number"
                step="any"
                placeholder={t('value', { ns: 'forms' })}
                disabled={hasSubmitted}
                onChange={(e) =>
                  formValidateAndSetNumber(form, e, 'valueBonus')
                }
              />
            </Form.Item>

            <Form.Item
              name="currencyBonus"
              label={t('currency', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', {
                    ns: 'forms',
                  }),
                },
              ]}
            >
              <Select
                mode="single"
                placeholder={t('currency', { ns: 'forms' })}
                className="w-full"
                loading={false}
                disabled={hasSubmitted}
                options={currencyOptions}
              />
            </Form.Item>
          </div>
        </div>

        <Form.Item
          label={t('dateOfRequestCreation', { ns: 'forms' })}
          name="dateOfRequestCreate"
        >
          <CustomDatePicker form={form} disabled={hasSubmitted} />
        </Form.Item>
      </div>

      {!hasSubmitted && (
        <FormActionButton isPending={isPending} onReset={onReset} />
      )}
    </Form>
  );
}
